/**
 * Two-way crew thread, its own dock tab.
 *
 * Same `#general` room as Mission Navigator. Sentinel stays the AI; this is
 * people in the vehicle. Status flips are filtered out so the pane stays talk.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress, ClickAwayListener, Popper, Typography } from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import {
  connectCrewChatStream,
  getCrewMediaObjectUrl,
  getCrewMessages,
  sendCrewImage,
  sendCrewMessage,
  type CrewChatMessage,
} from '../../services/crewChatApi';
import { useAuthStore } from '../../stores/authStore';
import {
  decodeChatText,
  inChatOrder,
  isCrewChatMessage,
  stripVehiclePriority,
  upsertMessage,
  vehicleLinePriority,
} from './crewChat';
import { formatWhen } from './format';
import { GOLD } from './missionLiveParts';
import DockStatStrip from './DockStatStrip';
import { DockTabHelp } from './DockTabHelp';
import ChatIcon from '@mui/icons-material/ChatBubbleOutline';
import PeopleIcon from '@mui/icons-material/PeopleOutline';
import ImageIcon from '@mui/icons-material/Image';
import CampaignIcon from '@mui/icons-material/Campaign';

const COMPOSE_EMOJIS = ['👍', '✅', '⚠️', '🎯', '👀', '🔥', '🚨', '📍', '❓', '😂'];

const CHANNEL = 'general';
const POLL_MS = 20_000;
const EDGE = '#262b33';
const BLUE = '#4ea1ff';

const CrewImage: React.FC<{ url?: string | null; alt: string }> = ({ url, alt }) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!url) return undefined;
    let revoked: string | null = null;
    let cancelled = false;
    getCrewMediaObjectUrl(url)
      .then((next) => {
        if (cancelled) {
          URL.revokeObjectURL(next);
          return;
        }
        revoked = next;
        setObjectUrl(next);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [url]);

  if (!url) return null;
  if (!objectUrl) return <CircularProgress size={14} sx={{ my: 0.5 }} />;
  return (
    <Box
      component="img"
      src={objectUrl}
      alt={alt}
      sx={{
        display: 'block',
        maxWidth: '100%',
        maxHeight: 140,
        borderRadius: 1,
        mb: 0.5,
      }}
    />
  );
};

const MissionCrewChat: React.FC = () => {
  const email = useAuthStore((s) => s.user?.email?.toLowerCase() || '');
  const [messages, setMessages] = useState<CrewChatMessage[]>([]);
  const [text, setText] = useState('');
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emojiEl, setEmojiEl] = useState<HTMLElement | null>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const stickToEnd = useRef(true);

  const visible = useMemo(
    () => inChatOrder(messages.filter(isCrewChatMessage)),
    [messages],
  );
  const glance = useMemo(() => {
    const hourAgo = Date.now() - 3600_000;
    return {
      people: new Set(visible.map((m) => m.user_email.toLowerCase())).size,
      images: visible.filter((m) => m.message_type === 'image').length,
      alerts: visible.filter((m) => m.message_type === 'alert').length,
      urgent: visible.filter((m) => vehicleLinePriority(decodeChatText(m.message || '')) === 'urgent').length,
      lastHour: visible.filter((m) => m.created_at && new Date(m.created_at).getTime() >= hourAgo).length,
      mine: visible.filter((m) => email && m.user_email.toLowerCase() === email).length,
    };
  }, [visible, email]);

  const merge = useCallback((incoming: CrewChatMessage) => {
    setMessages((prev) => upsertMessage(prev, incoming));
  }, []);

  const load = useCallback(async () => {
    try {
      const res = await getCrewMessages(CHANNEL, 50);
      setMessages(inChatOrder(res.messages || []));
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Could not load crew chat');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const poll = setInterval(load, POLL_MS);
    const es = connectCrewChatStream(CHANNEL, {
      onMessage: merge,
      onEdited: merge,
      onDeleted: ({ id }) => setMessages((prev) => prev.filter((m) => m.id !== id)),
      onOpen: () => setConnected(true),
      onError: () => setConnected(false),
    });
    return () => {
      clearInterval(poll);
      es.close();
    };
  }, [load, merge]);

  useEffect(() => {
    if (!stickToEnd.current || !scroller.current) return;
    scroller.current.scrollTop = scroller.current.scrollHeight;
  }, [visible]);

  const send = async () => {
    const message = text.trim();
    if (!message || sending || uploading) return;
    setSending(true);
    setError(null);
    try {
      merge(await sendCrewMessage(message, CHANNEL));
      setText('');
      stickToEnd.current = true;
    } catch (e: any) {
      setError(e?.message || 'Could not send');
    } finally {
      setSending(false);
    }
  };

  const sendImage = async (file: File) => {
    if (uploading || sending) return;
    setUploading(true);
    setError(null);
    try {
      merge(await sendCrewImage(file, CHANNEL, text.trim()));
      setText('');
      stickToEnd.current = true;
    } catch (e: any) {
      setError(e?.message || 'Could not send image');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        height: '100%',
        border: `1px solid ${EDGE}`,
        borderRadius: 1.25,
        overflow: 'hidden',
        bgcolor: 'rgba(0,0,0,0.15)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1.5,
          pt: 1.1,
          pb: 0.85,
          borderBottom: `1px solid ${EDGE}`,
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          Crew
        </Typography>
        <DockTabHelp tab="chat" />
        <Box sx={{ flex: 1 }} />
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            bgcolor: connected ? '#57c96b' : '#5b6570',
            boxShadow: connected ? '0 0 6px #57c96b' : 'none',
          }}
        />
        <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#5b6570' }}>
          {connected ? 'live' : 'polling'}
        </Typography>
      </Box>
      <Box sx={{ px: 1, pt: 0.85, pb: 0, borderBottom: `1px solid ${EDGE}` }}>
        <DockStatStrip
          embedded
          items={[
            { label: 'Messages', value: visible.length, color: '#e8ebee', icon: <ChatIcon /> },
            { label: 'Crew', value: glance.people, color: BLUE, icon: <PeopleIcon /> },
            { label: 'Images', value: glance.images, color: '#ce93d8', icon: <ImageIcon /> },
            { label: 'Alerts', value: glance.alerts, color: GOLD, icon: <CampaignIcon /> },
            { label: 'Urgent', value: glance.urgent, color: glance.urgent ? '#ef5350' : '#8a939f' },
            { label: 'Last hour', value: glance.lastHour, color: '#00b8a9' },
            { label: 'Mine', value: glance.mine, color: BLUE },
            { label: 'Link', value: connected ? 'live' : 'poll', color: connected ? '#57c96b' : '#8a939f' },
          ]}
        />
      </Box>

      <Box
        ref={scroller}
        onScroll={() => {
          const el = scroller.current;
          if (!el) return;
          stickToEnd.current = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
        }}
        sx={{
          flex: 1,
          minHeight: 180,
          overflowY: 'auto',
          px: 1.25,
          py: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {loading && visible.length === 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={18} />
          </Box>
        )}
        {!loading && visible.length === 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
            Same thread as the phones. Send a note to the vehicle.
          </Typography>
        )}
        {visible.map((msg, idx) => {
          const own = email && msg.user_email.toLowerCase() === email;
          const raw = decodeChatText(msg.message || '');
          const priority = vehicleLinePriority(raw);
          const body = priority ? stripVehiclePriority(raw) : raw;
          const prev = visible[idx - 1];
          const showName = !prev || prev.user_email !== msg.user_email;
          return (
            <Box
              key={msg.id}
              sx={{
                mt: showName ? 1 : 0.35,
                display: 'flex',
                flexDirection: 'column',
                alignItems: own ? 'flex-end' : 'flex-start',
              }}
            >
              {showName && (
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.58rem',
                    color: '#8a939f',
                    px: 0.4,
                    mb: 0.2,
                  }}
                >
                  {own ? 'You' : msg.user_name}
                  {msg.created_at ? ` · ${formatWhen(msg.created_at)}` : ''}
                </Typography>
              )}
              <Box
                sx={{
                  maxWidth: '86%',
                  px: 1.1,
                  py: 0.7,
                  borderRadius: own ? '12px 12px 3px 12px' : '12px 12px 12px 3px',
                  bgcolor: own ? 'rgba(78,161,255,0.22)' : 'rgba(255,255,255,0.05)',
                  border: `1px solid ${own ? 'rgba(78,161,255,0.35)' : EDGE}`,
                  color: own ? '#d7e9ff' : '#e8ebee',
                }}
              >
                {priority && (
                  <Typography
                    component="span"
                    sx={{
                      display: 'block',
                      width: 'fit-content',
                      mb: body && body !== 'Image' ? 0.4 : 0,
                      px: 0.55,
                      py: 0.05,
                      borderRadius: 0.5,
                      fontSize: '0.55rem',
                      fontWeight: 800,
                      letterSpacing: 0.6,
                      color: priority === 'urgent' ? '#ffb4b0' : '#c8d4e0',
                      bgcolor: priority === 'urgent' ? 'rgba(239,83,80,0.22)' : 'rgba(255,255,255,0.08)',
                    }}
                  >
                    {priority === 'urgent' ? 'URGENT' : 'NORMAL'}
                  </Typography>
                )}
                {msg.message_type === 'image' && (
                  <CrewImage url={msg.attachment_url} alt={msg.attachment_name || body || 'Image'} />
                )}
                {body && body !== 'Image' && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      fontSize: '0.74rem',
                      lineHeight: 1.4,
                    }}
                  >
                    {body}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>

      {error && (
        <Typography variant="caption" sx={{ display: 'block', px: 1.5, pb: 0.5, color: '#ef5350', fontSize: '0.62rem' }}>
          {error}
        </Typography>
      )}

      <Box
        sx={{
          display: 'flex',
          gap: 0.5,
          alignItems: 'center',
          px: 1.25,
          py: 1,
          borderTop: `1px solid ${EDGE}`,
        }}
      >
        <Box
          component="input"
          ref={fileInput}
          type="file"
          accept="image/*"
          hidden
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (!file) return;
            if (!file.type.startsWith('image/')) {
              setError('Please select an image');
              return;
            }
            void sendImage(file);
          }}
        />
        <Box
          component="button"
          type="button"
          disabled={sending || uploading}
          onClick={() => fileInput.current?.click()}
          aria-label="Attach image"
          sx={{
            width: 26,
            height: 26,
            p: 0,
            borderRadius: 1,
            border: `1px solid ${EDGE}`,
            bgcolor: 'transparent',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: uploading ? GOLD : '#8a939f',
            cursor: sending || uploading ? 'default' : 'pointer',
          }}
        >
          {uploading ? <CircularProgress size={12} sx={{ color: GOLD }} /> : <PhotoCameraIcon sx={{ fontSize: 15 }} />}
        </Box>
        <Box
          component="button"
          type="button"
          disabled={sending || uploading}
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            setEmojiEl((cur) => (cur ? null : e.currentTarget));
          }}
          aria-label="Insert emoji"
          sx={{
            width: 26,
            height: 26,
            p: 0,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 1,
            border: `1px solid ${EDGE}`,
            bgcolor: emojiEl ? 'rgba(245,185,66,0.12)' : 'transparent',
            color: emojiEl ? GOLD : '#8a939f',
            cursor: sending || uploading ? 'default' : 'pointer',
          }}
        >
          <SentimentSatisfiedAltIcon sx={{ fontSize: 16 }} />
        </Box>
        <Popper open={!!emojiEl} anchorEl={emojiEl} placement="top-start" sx={{ zIndex: 1600 }}>
          <ClickAwayListener onClickAway={() => setEmojiEl(null)}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(5, 1fr)',
                gap: 0.25,
                p: 0.6,
                mb: 0.5,
                borderRadius: 1,
                bgcolor: 'rgba(12,16,22,0.96)',
                border: `1px solid ${EDGE}`,
              }}
            >
              {COMPOSE_EMOJIS.map((emoji) => (
                <Box
                  key={emoji}
                  component="button"
                  type="button"
                  onClick={() => {
                    setText((cur) => `${cur}${emoji}`);
                    setEmojiEl(null);
                  }}
                  sx={{
                    width: 28,
                    height: 28,
                    p: 0,
                    border: 'none',
                    borderRadius: 0.75,
                    bgcolor: 'transparent',
                    fontSize: '1rem',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
                  }}
                >
                  {emoji}
                </Box>
              ))}
            </Box>
          </ClickAwayListener>
        </Popper>
        <Box
          component="input"
          value={text}
          disabled={sending || uploading}
          placeholder="Message the crew"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setText(e.target.value)}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
          }}
          sx={{
            flex: 1,
            height: 30,
            px: 1,
            borderRadius: 1,
            border: `1px solid ${EDGE}`,
            bgcolor: 'rgba(0,0,0,0.25)',
            color: 'text.primary',
            font: 'inherit',
            fontSize: '0.75rem',
            outline: 'none',
            '&:focus': { borderColor: GOLD },
          }}
        />
        <Box
          component="button"
          type="button"
          disabled={sending || uploading || !text.trim()}
          onClick={() => void send()}
          sx={{
            height: 26,
            px: 1.25,
            borderRadius: 1,
            border: `1px solid ${EDGE}`,
            bgcolor: text.trim() ? 'rgba(78,161,255,0.18)' : 'transparent',
            color: text.trim() ? BLUE : '#5b6570',
            font: 'inherit',
            fontSize: '0.64rem',
            fontWeight: 700,
            cursor: sending || uploading || !text.trim() ? 'default' : 'pointer',
          }}
        >
          {sending ? '…' : 'Send'}
        </Box>
      </Box>
    </Box>
  );
};

export default MissionCrewChat;
